import * as React from 'react'
import { ActionButton } from 'uiComponents/buttons'
import { ModalDialog, ModalDialogTitle, NavigationSection } from 'uiComponents/popups/modal'
import { ChartDataLoader } from 'uiComponents/loaders'
import { SingleSelectFieldFormik } from 'uiComponents/input'
import { BarcodePoolFormat, BarcodePoolType } from 'venue/bookingCodes/bookingCodesService'
import TextInputFieldFormik from '../../../uiComponents/input/textInput/textInputFieldFormik'
import { barcodeFormatOptions, getPoolTypeOptions } from './common'
import { FormWrapper } from '../../../uiComponents/form/form'
import { validationSchema } from './validationSchema'
import { useTranslation } from 'translations'

function isPrefixFieldVisible(allowPrefix: boolean, type: BarcodePoolType | null) {
    return type === 'Generated' && allowPrefix
}

type FormValues = {
    barcodePoolName: string
    barcodeFormatOption: { type: BarcodePoolFormat; allowPrefix: boolean; allowGenerated: boolean } | null
    poolTypeOption: BarcodePoolType | null
    barcodePrefix: string
}

interface DialogProps {
    accountSlug: string
    loading: boolean
    onCancel: () => void
    onConfirm: (name: string, format: BarcodePoolFormat, type: BarcodePoolType, prefix: string) => void
}

export function NewPoolDialog({ onConfirm, loading, onCancel }: DialogProps) {
    const { t } = useTranslation()
    const onSubmit = (values: FormValues) => {
        const { barcodePoolName, barcodeFormatOption, poolTypeOption, barcodePrefix } = values
        onConfirm(barcodePoolName, barcodeFormatOption!.type, poolTypeOption!, barcodePrefix)
    }

    return (
        <ModalDialog onDismiss={onCancel} interactive fromTop="20%">
            {loading && <ChartDataLoader />}
            <div style={{ width: '25em' }}>
                <ModalDialogTitle>{t('venue.bookingCodes.barcodePools.newPool.title')}</ModalDialogTitle>
                <FormWrapper
                    enableReinitialize={true}
                    initialValues={{
                        barcodePoolName: '',
                        barcodePrefix: '',
                        poolTypeOption: null,
                        barcodeFormatOption: null,
                    }}
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                >
                    {({ values, isSubmitting }) => (
                        <>
                            <TextInputFieldFormik
                                id="barcodePoolName"
                                name="barcodePoolName"
                                label={t('venue.bookingCodes.barcodePools.newPool.nameLabel')}
                                maxLength={100}
                                placeholder={t('venue.bookingCodes.barcodePools.newPool.namePlaceholder')}
                                type="text"
                            />
                            <SingleSelectFieldFormik
                                id="barcodeFormatOption"
                                name="barcodeFormatOption"
                                label={t('venue.bookingCodes.barcodePools.newPool.formatLabel')}
                                options={barcodeFormatOptions}
                                height="2.5rem"
                                noSelectOption={t('venue.bookingCodes.barcodePools.newPool.formatPlaceholder')}
                            />
                            {isPrefixFieldVisible(
                                values.barcodeFormatOption?.allowPrefix ?? false,
                                values.poolTypeOption,
                            ) && (
                                <TextInputFieldFormik
                                    id="barcodePrefix"
                                    name="barcodePrefix"
                                    label={t('venue.bookingCodes.barcodePools.newPool.prefixLabel')}
                                    height="2.5rem"
                                    placeholder={t('venue.bookingCodes.barcodePools.newPool.prefixLabel')}
                                    maxLength={8}
                                />
                            )}
                            <SingleSelectFieldFormik
                                id="poolTypeOption"
                                name="poolTypeOption"
                                label={t('venue.bookingCodes.barcodePools.newPool.poolTypeLabel')}
                                options={getPoolTypeOptions()}
                                height="2.5rem"
                                noSelectOption={t('venue.bookingCodes.barcodePools.newPool.poolTypePlaceholder')}
                            />
                            <NavigationSection>
                                <ActionButton size="large" secondary onClick={onCancel}>
                                    {t('common.cancel')}
                                </ActionButton>
                                <ActionButton
                                    disabled={isSubmitting}
                                    id="add-new-barcode-pool-button"
                                    size="large"
                                    type="submit"
                                >
                                    {t('common.save')}
                                </ActionButton>
                            </NavigationSection>
                        </>
                    )}
                </FormWrapper>
            </div>
        </ModalDialog>
    )
}
